import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// A save failure must be seen. The editors render their error inline above
// the form, but Save sits at the bottom of a long form, so after scrolling
// down to click it the message appeared off-screen (found on dev with the
// version-conflict 409: the person saw nothing happen). This dialog sits on
// top of the page, styled like NavigationBlocker's prompt. For a conflict it
// offers "Reload page" (the only way to see the other person's version) and
// "Keep editing" (so text can be copied first); for anything else, "OK".
// It hides itself when dismissed and shows again for the next distinct error.
export const isConflictError = (message, status) =>
  status === 409 || /saved changes to this section/i.test(message || '');

const font = { fontFamily: 'Montserrat, sans-serif' };
const buttonBase = { ...font, padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' };
const secondaryButton = { ...buttonBase, border: '1px solid #e0e0e0', background: '#fff', color: '#333' };
const primaryButton = { ...buttonBase, border: 'none', background: '#0784a7', color: '#fff', fontWeight: 600 };

const SaveErrorDialog = ({ message, status, onReload }) => {
  const [dismissedFor, setDismissedFor] = useState(null);
  // A new error (different text) shows the dialog again even if an earlier
  // one was dismissed.
  useEffect(() => { setDismissedFor(null); }, [message]);

  if (!message || dismissedFor === message) return null;
  const conflict = isConflictError(message, status);
  const dismiss = () => setDismissedFor(message);
  const reload = () => {
    if (onReload) onReload();
    else if (typeof window !== 'undefined' && window.location) window.location.reload();
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-error-title"
      data-testid="save-error-dialog"
      onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') dismiss(); }}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
    >
      <div style={{ background: '#fff', borderRadius: '10px', width: '90%', maxWidth: '450px', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)', overflow: 'hidden' }}>
        <div style={{ ...font, padding: '24px', fontSize: '15px', color: '#1a2b3d', lineHeight: '1.6' }}>
          <div id="save-error-title" style={{ fontWeight: 600, marginBottom: '8px' }}>
            {conflict ? 'Your changes were not saved' : 'Save failed'}
          </div>
          <div>{message}</div>
        </div>
        <div style={{ padding: '12px 24px', borderTop: '1px solid #e8e8e8', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          {conflict ? (
            <>
              <button type="button" onClick={dismiss} style={secondaryButton}>Keep editing</button>
              <button type="button" onClick={reload} autoFocus style={primaryButton}>Reload page</button>
            </>
          ) : (
            <button type="button" onClick={dismiss} autoFocus style={primaryButton}>OK</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SaveErrorDialog;
